import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Send, Plus, Trash2, User, AlertTriangle, Users, ExternalLink, Clock, XCircle, Info, CheckCircle } from 'lucide-react'
import { useOfficeDocument } from '../../hooks/useOfficeDocument'
import { useSendDocument } from '../../hooks/useDocuments'
import { usePartySearch, useRecentParties } from '../../hooks/usePartySearch'
import { useSignaturePlacement } from '../../hooks/useSignaturePlacement'
import { useDocumentBinding } from '../../hooks/useDocumentBinding'
import { SignatureFieldPlacer } from '../signing/SignatureFieldPlacer'
import { MetadataPanel } from '../metadata/MetadataPanel'
import { TagPicker } from '../organize/TagPicker'
import { updateDocumentMetadata } from '../../services/documentsApi'
import { addTagToDocument } from '../../services/tagsApi'
import { readContentControls, ccValuesToMetadata, mergeMetadata } from '../../hooks/useContentControls'
import { writeXmlMetadata } from '../../hooks/useCustomXmlParts'
import { InlineHint } from '../common/InlineHint'
import { ContextualTip, markActionCompleted } from '../common/ContextualTip'
import { devLog } from '../../services/devLog'
import { MAIN_APP_URL } from '../../config'
import type { DocumentMetadata } from '../metadata/MetadataPanel'
import { MemberPicker } from './MemberPicker'
import { AnimatedCheckmark } from '../common/AnimatedCheckmark'
import { getSignerColor } from '../../hooks/signerColors'
import type { Actor } from '../../hooks/useActors'

interface Signer {
  name: string
  email: string
  partyKoblinkId?: string
}

interface SendDocumentProps {
  actorId?: string
  actors?: Actor[]
  onActorChange?: (actorId: string) => void
}

/**
 * "Send this document to signing" flow.
 *
 * Features:
 * - Contact autocomplete in email fields
 * - Recent contacts quick-select
 * - Signature field placement via text tags
 * - Post-send save-contact prompt
 */
export function SendDocument({ actorId, actors }: SendDocumentProps) {
  const { t } = useTranslation()
  const { getDocumentBlob, isReading } = useOfficeDocument()
  const sendDocument = useSendDocument()
  const { state: bindingState, binding, document: boundDoc, bind, unbind } = useDocumentBinding(actorId)
  // Note: party preferences are auto-created by backend on signature send
  const { tier, placeSigner, readPlacements, removePlacement, removePlacements, isPlacing, placementError } = useSignaturePlacement()

  const [title, setTitle] = useState('')
  const [signers, setSigners] = useState<Signer[]>([{ name: '', email: '' }])
  const [signingMode, setSigningMode] = useState<'parallel' | 'sequential'>('parallel')
  const [sent, setSent] = useState(false)

  // Contact search state (per-signer debounced queries)
  const [searchQueries, setSearchQueries] = useState<Record<number, string>>({})
  const [debouncedQueries, setDebouncedQueries] = useState<Record<number, string>>({})
  const [activeDropdownIndex, setActiveDropdownIndex] = useState<number | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Metadata + tags state
  const [isMetadataExpanded, setIsMetadataExpanded] = useState(false)
  const [metadata, setMetadata] = useState<DocumentMetadata>({})
  const [selectedTags, setSelectedTags] = useState<number[]>([])

  // Signature placement state
  const [placedSigners, setPlacedSigners] = useState<Set<string>>(new Set())

  // Post-send state
  const [showSavePrompt, setShowSavePrompt] = useState(false)
  const [unsavedSigners, setUnsavedSigners] = useState<Signer[]>([])
  const [showCleanupPrompt, setShowCleanupPrompt] = useState(false)
  const [showMemberPicker, setShowMemberPicker] = useState(false)

  const activeActor = actors?.find((a) => a.koblink_id === actorId)
  const orgKoblinkId = activeActor?.actor_type === 'ORGANIZATION' ? actorId : undefined

  // Contact search (uses debounced query for active dropdown)
  const activeQuery = activeDropdownIndex !== null ? debouncedQueries[activeDropdownIndex] || '' : ''
  const { data: searchResults } = usePartySearch(activeQuery, actorId)
  const { data: recentContacts } = useRecentParties(actorId)

  // Debounce search queries (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQueries({ ...searchQueries })
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQueries])

  // Pre-fill title from draft binding (sync from external async source)
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { if (bindingState === 'bound-draft' && binding?.title && !title) setTitle(binding.title) }, [bindingState, binding?.title]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdowns on outside click
  useEffect(() => {
    if (activeDropdownIndex === null) return
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdownIndex(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [activeDropdownIndex])

  const addSigner = () => {
    setSigners((prev) => [...prev, { name: '', email: '' }])
  }

  const removeSigner = (index: number) => {
    setSigners((prev) => prev.filter((_, i) => i !== index))
    setActiveDropdownIndex(null)
  }

  const updateSigner = (index: number, field: keyof Signer, value: string) => {
    setSigners((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value, partyKoblinkId: undefined } : s)),
    )
    if (field === 'email') {
      setSearchQueries((prev) => ({ ...prev, [index]: value }))
      if (value.length >= 2) {
        setActiveDropdownIndex(index)
      } else {
        setActiveDropdownIndex(null)
      }
    }
  }

  const selectContact = (index: number, party: { koblink_id: string; email?: string | null; display_name: string }) => {
    setSigners((prev) =>
      prev.map((s, i) =>
        i === index
          ? { name: party.display_name, email: party.email || '', partyKoblinkId: party.koblink_id }
          : s,
      ),
    )
    setActiveDropdownIndex(null)
    setSearchQueries((prev) => ({ ...prev, [index]: '' }))
  }

  const addSignerFromContact = useCallback((contact: { name: string; email: string }) => {
    // Check if already added
    if (signers.some((s) => s.email.toLowerCase() === contact.email.toLowerCase())) return

    // Fill first empty signer slot, or add new
    const emptyIndex = signers.findIndex((s) => !s.name && !s.email)
    if (emptyIndex >= 0) {
      setSigners((prev) =>
        prev.map((s, i) =>
          i === emptyIndex
            ? { name: contact.name, email: contact.email }
            : s,
        ),
      )
    } else {
      setSigners((prev) => [
        ...prev,
        { name: contact.name, email: contact.email },
      ])
    }
  }, [signers])

  const handlePlaceSigner = async (email: string, name: string, colorIndex: number) => {
    try {
      await placeSigner(email, name, colorIndex)
      setPlacedSigners((prev) => new Set(prev).add(email))
    } catch {
      // placementError is set by useSignaturePlacement — shown in UI
    }
  }

  const handleRemoveSigner = async (email: string) => {
    try {
      await removePlacement(email)
    } catch {
      // Best effort
    }
    setPlacedSigners((prev) => {
      const next = new Set(prev)
      next.delete(email)
      return next
    })
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const validSigners = signers.filter((s) => s.name.trim() && s.email.trim() && emailRegex.test(s.email.trim()))
  const hasTitle = title.trim().length > 0
  const canSend = hasTitle && validSigners.length > 0 && !sendDocument.isPending && !isReading
  const hasUnplacedSigners = validSigners.length > 0 && validSigners.some((s) => !placedSigners.has(s.email))
  const hasInvalidEmails = signers.some((s) => s.email.trim() && !emailRegex.test(s.email.trim()))

  // Build disabled reason for tooltip
  const disabledReason = !hasTitle
    ? t('sign.disabledNoTitle', 'Add a document title')
    : validSigners.length === 0
      ? hasInvalidEmails
        ? t('sign.disabledInvalidEmail', 'Check email addresses')
        : t('sign.disabledNoSigners', 'Add at least one signer')
      : null

  const handleSend = async () => {
    try {
      const blob = await getDocumentBlob()
      const filename = `${title.trim()}.docx`

      const result = await sendDocument.mutateAsync({
        file: blob,
        filename,
        title: title.trim(),
        signers: validSigners,
        signing_mode: signingMode,
        actorId,
        readPlacements,
      })

      // Read Content Controls from document and merge with panel metadata
      const koblinkId = result.document.koblink_id
      let finalMetadata = metadata
      try {
        const ccValues = await readContentControls()
        const ccMetadata = ccValuesToMetadata(ccValues)
        if (Object.keys(ccMetadata).length > 0) {
          finalMetadata = mergeMetadata(metadata, ccMetadata)
          devLog.info('CC merge', `Merged ${Object.keys(ccMetadata).length} CC field(s) with panel`)
        }
      } catch (e) {
        devLog.warn('CC merge', `Failed to read CCs: ${e}`)
      }

      // Write metadata as Custom XML Part in DOCX (best-effort)
      // Always include owner_actor_id so auto-switch works on reopen
      try {
        const xmlData: Record<string, string> = {}
        if (finalMetadata.counterparties?.length) xmlData.counterparty = finalMetadata.counterparties.join(', ')
        if (finalMetadata.contract_value) xmlData.contract_value = finalMetadata.contract_value
        if (finalMetadata.contract_currency) xmlData.contract_currency = finalMetadata.contract_currency
        if (finalMetadata.contract_expires_at) xmlData.contract_expires_at = finalMetadata.contract_expires_at
        if (actorId) xmlData.owner_actor_id = actorId
        if (Object.keys(xmlData).length > 0) {
          await writeXmlMetadata(xmlData)
        }
      } catch (e) {
        devLog.warn('XML Parts', `Write failed (non-blocking): ${e}`)
      }

      // Write document binding so panel detects this doc on next open
      await bind(koblinkId, title.trim(), 'pending', actorId)

      // Apply metadata after successful send (non-blocking)
      const metadataPayload: Record<string, unknown> = {}
      if (finalMetadata.folder_id != null) metadataPayload.folder = finalMetadata.folder_id
      if (finalMetadata.document_type_id != null) metadataPayload.document_type_id = finalMetadata.document_type_id
      if (finalMetadata.department_id != null) metadataPayload.department = finalMetadata.department_id
      if (finalMetadata.responsible_person) metadataPayload.responsible_person = finalMetadata.responsible_person
      if (finalMetadata.contract_value) metadataPayload.contract_value = finalMetadata.contract_value
      if (finalMetadata.contract_currency) metadataPayload.contract_currency = finalMetadata.contract_currency
      if (finalMetadata.contract_expires_at) metadataPayload.contract_expires_at = finalMetadata.contract_expires_at
      if (finalMetadata.counterparties && finalMetadata.counterparties.length > 0) metadataPayload.counterparties = finalMetadata.counterparties

      if (Object.keys(metadataPayload).length > 0) {
        try {
          await updateDocumentMetadata(koblinkId, metadataPayload, actorId)
          devLog.info('Metadata', `Saved ${Object.keys(metadataPayload).length} field(s)`)
        } catch (e) {
          devLog.warn('Metadata', `Failed to save metadata: ${e}`)
        }
      }

      // Apply tags (non-blocking)
      for (const tagId of selectedTags) {
        try {
          await addTagToDocument(koblinkId, tagId, actorId)
        } catch (e) {
          devLog.warn('Tags', `Failed to add tag ${tagId}: ${e}`)
        }
      }

      // Party preferences are auto-created by backend when signature is sent.
      // No need to prompt user to save contacts manually.
      if (placedSigners.size > 0) {
        setShowCleanupPrompt(true)
      } else {
        setSent(true)
      }
    } catch {
      // Error is available via sendDocument.error
    }
  }

  const handleSaveContacts = async () => {
    // Party preferences are auto-created by backend — just proceed
    setShowSavePrompt(false)
    if (placedSigners.size > 0) {
      setShowCleanupPrompt(true)
    } else {
      setSent(true)
    }
  }

  const handleCleanup = async (remove: boolean) => {
    if (remove) {
      try {
        await removePlacements()
      } catch {
        // Best effort
      }
    }
    setShowCleanupPrompt(false)
    setSent(true)
  }

  const resetForm = () => {
    setSent(false)
    setShowSavePrompt(false)
    setShowCleanupPrompt(false)
    setTitle('')
    setSigners([{ name: '', email: '' }])
    setPlacedSigners(new Set())
    setUnsavedSigners([])
    setMetadata({})
    setSelectedTags([])
    setIsMetadataExpanded(false)
  }

  // --- Binding-aware UI states ---
  // These render INSTEAD of the form when the document is already linked to a KobSign process.

  // Loading binding check (fast — typically <200ms)
  if (bindingState === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center flex-1 gap-2 px-4 py-6">
        <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" />
        <p className="text-xs text-neutral-400">{t('common.loading')}</p>
      </div>
    )
  }

  // Active signing/negotiation — document is locked
  if (bindingState === 'bound-active' && binding) {
    const isNegotiating = boundDoc?.status === 'negotiating'
    const statusLabel = isNegotiating
      ? t('binding.negotiationInProgress')
      : t('binding.signingInProgress')
    const statusHint = isNegotiating
      ? t('binding.alreadyNegotiating')
      : t('binding.alreadySigning')

    return (
      <div className="flex flex-col items-center justify-center flex-1 px-4 py-6 text-center">
        <Clock size={32} className="text-primary-500 mb-3" />
        <h2 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 mb-1">
          {statusLabel}
        </h2>
        <p className="text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-2 truncate max-w-full">
          {binding.title}
        </p>
        {boundDoc && (
          <p className="text-[10px] text-neutral-500 dark:text-neutral-400 mb-1">
            {t(`status.signingStatus.${boundDoc.status}`)}
            {boundDoc.signer_count > 0 && ` · ${t('binding.signedOf', { signed: boundDoc.signed_count, total: boundDoc.signer_count })}`}
          </p>
        )}
        <div className="flex gap-2 mt-4 w-full">
          <a
            href={`${MAIN_APP_URL}/documents/${binding.koblink_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-xs rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <ExternalLink size={12} />
            {t('binding.viewInKobSign')}
          </a>
        </div>
        <p className="mt-4 text-[10px] text-neutral-400 dark:text-neutral-500 px-2">
          {statusHint}
        </p>
      </div>
    )
  }

  // Terminal state — signing is done (completed, cancelled, expired, etc.)
  if (bindingState === 'bound-terminal' && binding) {
    const statusMap: Record<string, { icon: typeof CheckCircle; color: string; label: string }> = {
      completed: { icon: CheckCircle, color: 'text-success-500', label: t('binding.signingCompleted') },
      declined: { icon: XCircle, color: 'text-error-500', label: t('binding.signingDeclined') },
      recalled: { icon: XCircle, color: 'text-neutral-400', label: t('binding.signingRecalled') },
      expired: { icon: Clock, color: 'text-amber-500', label: t('binding.signingExpired') },
    }
    const status = boundDoc?.status ?? binding.status
    const { icon: StatusIcon, color, label } = statusMap[status] ?? statusMap.completed

    return (
      <div className="flex flex-col items-center justify-center flex-1 px-4 py-6 text-center">
        <StatusIcon size={32} className={`${color} mb-3`} />
        <h2 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 mb-1">
          {label}
        </h2>
        <p className="text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-3 truncate max-w-full">
          {binding.title}
        </p>
        <a
          href={`${MAIN_APP_URL}/documents/${binding.koblink_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline mb-4"
        >
          <ExternalLink size={12} />
          {t('binding.viewInKobSign')}
        </a>
        <button
          onClick={unbind}
          className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
        >
          {t('binding.startFresh')}
        </button>
        <p className="mt-1 text-[10px] text-neutral-400">
          {t('binding.startFreshDescription')}
        </p>
      </div>
    )
  }

  // No access — document belongs to someone else
  if (bindingState === 'no-access' && binding) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 px-4 py-6 text-center">
        <AlertTriangle size={32} className="text-amber-500 mb-3" />
        <h2 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 mb-1">
          {binding.title}
        </h2>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
          {t('binding.noAccess')}
        </p>
        <button
          onClick={unbind}
          className="text-xs text-neutral-500 hover:text-primary-600 transition-colors"
        >
          {t('binding.unlinkDocument')}
        </button>
      </div>
    )
  }

  // Draft state — show a banner above the normal form
  const draftBanner = bindingState === 'bound-draft' && binding ? (
    <div className="mb-2 flex items-start gap-1.5 px-2 py-1.5 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
      <Info size={12} className="shrink-0 mt-0.5 text-blue-500" />
      <div className="text-[10px] text-blue-700 dark:text-blue-400">
        <p>{t('binding.draftExists')}</p>
        <a
          href={`${MAIN_APP_URL}/documents/${binding.koblink_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 hover:underline mt-0.5"
        >
          <ExternalLink size={10} />
          {t('binding.viewInKobSign')}
        </a>
      </div>
    </div>
  ) : null

  // Save contacts prompt
  if (showSavePrompt) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 px-4 py-6 text-center">
        <CheckCircle size={32} className="text-success-500 mb-3" />
        <h2 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 mb-3">
          {t('contacts.saveNewContacts')}
        </h2>
        <div className="w-full space-y-1.5 mb-4">
          {unsavedSigners.map((s) => (
            <div key={s.email} className="text-xs text-neutral-600 dark:text-neutral-400 px-2 py-1 bg-neutral-50 dark:bg-neutral-800 rounded">
              {s.name} ({s.email})
            </div>
          ))}
        </div>
        <div className="flex gap-2 w-full">
          <button
            onClick={() => {
              setShowSavePrompt(false)
              if (placedSigners.size > 0) {
                setShowCleanupPrompt(true)
              } else {
                setSent(true)
              }
            }}
            className="flex-1 px-3 py-2 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800"
          >
            {t('contacts.skipSave')}
          </button>
          <button
            onClick={handleSaveContacts}
            className="flex-1 px-3 py-2 text-xs rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {t('contacts.saveContact')}
          </button>
        </div>
      </div>
    )
  }

  // Cleanup prompt (remove signature tags from document)
  if (showCleanupPrompt) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 px-4 py-6 text-center">
        <CheckCircle size={32} className="text-success-500 mb-3" />
        <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-4">
          {t('signature.removeAfterSend')}
        </p>
        <div className="flex gap-2 w-full">
          <button
            onClick={() => handleCleanup(false)}
            className="flex-1 px-3 py-2 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800"
          >
            {t('signature.keep')}
          </button>
          <button
            onClick={() => handleCleanup(true)}
            className="flex-1 px-3 py-2 text-xs rounded-md bg-primary-600 text-white hover:bg-primary-700"
          >
            {t('signature.remove')}
          </button>
        </div>
      </div>
    )
  }

  // Success state
  if (sent) {
    markActionCompleted('first-sign')
    return (
      <div className="flex flex-col items-center justify-center flex-1 px-6 py-8 text-center animate-fade-in">
        <AnimatedCheckmark size={48} className="mb-5" />
        <h2 className="text-[15px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100 mb-1">
          {t('sign.documentSent')}
        </h2>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 max-w-[220px]">
          {t('sign.documentSentDescription')}
        </p>
        <button
          onClick={resetForm}
          className="mt-5 px-4 py-2 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 active:scale-[0.98] transition-all"
        >
          {t('sign.sendToSign')}
        </button>
      </div>
    )
  }

  const errorMessage = sendDocument.error instanceof Error
    ? sendDocument.error.message
    : null

  // Check if first signer is empty (for showing recent contacts)
  const firstSignerEmpty = !signers[0]?.name && !signers[0]?.email

  return (
    <div className="flex flex-col flex-1 px-3 py-3 overflow-y-auto">
      <h2 className="text-[15px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100 mb-2">
        {t('sign.title')}
      </h2>

      {draftBanner}

      <div className="mb-2 space-y-2">
        <InlineHint hintKey="signing-intro">
          {t('hints.signingIntro')}
        </InlineHint>
        <ContextualTip context="first-sign">
          {t('tips.firstSign')}
        </ContextualTip>
      </div>

      {/* Document title */}
      <div className="mb-3 section-divider pb-3">
        <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5 block">
          {t('sign.documentTitle')}
        </label>
        <input
          type="text"
          placeholder={t('sign.documentTitlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
        />
      </div>

      {/* Signing mode toggle */}
      <div className="mb-4">
        <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5 block">
          {t('sign.signingMode')}
        </label>
        <div className="flex gap-2">
          {(['parallel', 'sequential'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setSigningMode(mode)}
              className={`flex-1 py-1.5 px-2 text-xs rounded-md border transition-colors ${
                signingMode === mode
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:border-neutral-300'
              }`}
            >
              {t(`sign.${mode}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Document metadata (collapsible) */}
      <MetadataPanel
        actorId={actorId}
        orgKoblinkId={orgKoblinkId}
        isExpanded={isMetadataExpanded}
        onToggle={() => setIsMetadataExpanded(!isMetadataExpanded)}
        metadata={metadata}
        onMetadataChange={setMetadata}
      />

      {/* Tags */}
      <div className="mb-3">
        <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5 block">
          {t('organize.tags')}
        </label>
        <TagPicker
          actorId={actorId}
          selectedTagIds={selectedTags}
          onToggle={(tagId) => {
            setSelectedTags((prev) =>
              prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
            )
          }}
        />
      </div>

      {/* Recent contacts */}
      {firstSignerEmpty && recentContacts && recentContacts.length > 0 && (
        <div className="mb-3">
          <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5 block">
            {t('contacts.recentContacts')}
          </label>
          <div className="flex flex-wrap gap-1">
            {recentContacts.map((party) => (
              <button
                key={party.koblink_id}
                onClick={() => addSignerFromContact({ name: party.display_name, email: party.email || '' })}
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded-full border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:border-primary-300 dark:hover:border-primary-700 transition-colors"
              >
                <User size={10} className="shrink-0" />
                <span className="truncate max-w-[120px]">{party.display_name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Signers list */}
      <div className="mb-3 relative border-t border-neutral-100 dark:border-neutral-800 pt-3 mt-1">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300">
            {t('sign.addSigners')}
          </label>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setShowMemberPicker((prev) => !prev)}
            className="flex items-center gap-1 text-[10px] text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            <Users size={11} />
            {t('contacts.browseMembers')}
          </button>
        </div>
        {showMemberPicker && (
          <MemberPicker
            actorId={actorId}
            orgKoblinkId={orgKoblinkId}
            existingEmails={signers.map((s) => s.email).filter(Boolean)}
            onSelect={(name, email) => {
              addSignerFromContact({ name, email })
              setShowMemberPicker(false)
            }}
            onClose={() => setShowMemberPicker(false)}
          />
        )}
        <div className="space-y-2">
          {signers.map((signer, index) => {
            const color = getSignerColor(index)
            return (
              <div
                key={index}
                className="signer-card rounded-lg p-3 bg-neutral-50 dark:bg-neutral-800/50 animate-fade-in"
                style={{ borderLeftColor: color.hex }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-semibold text-neutral-500 dark:text-neutral-400">
                    {t('sign.signerLabel', { number: index + 1, defaultValue: `Signer ${index + 1}` })}
                  </span>
                  {signers.length > 1 && (
                    <button
                      onClick={() => removeSigner(index)}
                      className="p-0.5 rounded text-neutral-400 hover:text-error-500 transition-colors"
                      aria-label={t('templates.removeSigner')}
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
                <div className="space-y-1.5">
                  <input
                    type="text"
                    placeholder={t('templates.signerName')}
                    value={signer.name}
                    onChange={(e) => updateSigner(index, 'name', e.target.value)}
                    className="w-full px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
                  />
                  {/* Email input with contact autocomplete */}
                  <div className="relative" ref={activeDropdownIndex === index ? dropdownRef : undefined}>
                    <input
                      type="email"
                      placeholder={t('contacts.searchContacts')}
                      value={signer.email}
                      onChange={(e) => updateSigner(index, 'email', e.target.value)}
                      onFocus={() => {
                        if (signer.email.length >= 2) setActiveDropdownIndex(index)
                      }}
                      className="w-full px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
                    />
                    {/* Contact search dropdown */}
                    {activeDropdownIndex === index && searchResults && searchResults.length > 0 && (
                      <div className="absolute left-0 top-full mt-1 w-full bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-50 py-1 max-h-[180px] overflow-y-auto">
                        {searchResults.map((party) => (
                          <button
                            key={party.koblink_id}
                            onClick={() => selectContact(index, party)}
                            className="w-full px-2 py-1.5 text-left hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
                          >
                            <p className="text-xs font-medium text-neutral-900 dark:text-neutral-100 truncate">
                              {party.display_name}
                            </p>
                            <p className="text-[10px] text-neutral-500 dark:text-neutral-400 truncate">
                              {party.email}
                              {party.organization && ` · ${party.organization.display_name}`}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Signature field placer */}
                  <SignatureFieldPlacer
                    email={signer.email}
                    name={signer.name}
                    colorIndex={index}
                    onPlace={() => handlePlaceSigner(signer.email, signer.name, index)}
                    onRemove={() => handleRemoveSigner(signer.email)}
                    isPlaced={placedSigners.has(signer.email)}
                    isPlacing={isPlacing}
                    disabled={!signer.name.trim() || !signer.email.trim()}
                    tier={tier}
                  />
                  {/* Placement status badge */}
                  {signer.email && placedSigners.has(signer.email) && (
                    <div className="flex items-center gap-1 text-[10px] text-success-600 dark:text-success-400">
                      <CheckCircle size={10} />
                      <span>{t('signature.placed')}</span>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
        <button
          onClick={addSigner}
          className="mt-2 flex items-center gap-1 text-[13px] text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
        >
          <Plus size={12} />
          {t('templates.addSigner')}
        </button>
      </div>

      {/* Placement error */}
      {placementError && (
        <div className="flex items-start gap-1.5 mb-2 px-2 py-1.5 rounded-md bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800">
          <AlertTriangle size={12} className="shrink-0 mt-0.5 text-error-500" />
          <p className="text-[10px] text-error-700 dark:text-error-400">
            {placementError}
          </p>
        </div>
      )}

      {/* Signature placement warning */}
      {hasUnplacedSigners && (
        <div className="flex items-start gap-1.5 mb-3 px-2 py-1.5 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <AlertTriangle size={12} className="shrink-0 mt-0.5 text-amber-500" />
          <p className="text-[10px] text-amber-700 dark:text-amber-400">
            {t('signature.notPlacedWarning')}
          </p>
        </div>
      )}

      {/* Invalid email warning */}
      {hasInvalidEmails && (
        <div className="flex items-start gap-1.5 mb-2 px-2 py-1.5 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <AlertTriangle size={12} className="shrink-0 mt-0.5 text-amber-500" />
          <p className="text-[10px] text-amber-700 dark:text-amber-400">
            {t('sign.invalidEmailWarning', 'One or more email addresses are invalid.')}
          </p>
        </div>
      )}

      {/* Error */}
      {errorMessage && (
        <p className="text-xs text-error-500 mb-3">
          {t('sign.sendError', { defaultValue: errorMessage })}
        </p>
      )}

      {/* Send button */}
      <button
        onClick={handleSend}
        disabled={!canSend}
        title={disabledReason || undefined}
        className="mt-auto flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium active:scale-[0.98] transition-all shadow-sm hover:shadow-md"
      >
        {isReading || sendDocument.isPending ? (
          <>
            {isReading ? t('sign.uploadingDocument') : t('common.sending')}
          </>
        ) : (
          <>
            <Send size={14} />
            {t('sign.sendDocument')}
          </>
        )}
      </button>
    </div>
  )
}
